import * as React from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import Form from 'antd/lib/form';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import { convertBookingNotesToZip } from 'src/api/booking';
import Timeline from 'antd/lib/timeline';
import Card from 'antd/lib/card';
import { getDateValue, isValidArray, isValidNumber } from 'src/Library/Helper';
import { prettifyString } from 'src/utils/utils';
import { InquiryModes, InquiryType } from 'src/Library/Constants';
import { StylesProps } from 'src/Library/Types';
import { getTrailerOrDomesticVehicleList, renderAddressDetails, renderoverWeight } from './Cards/InquiryCommon';
import { ContainerList } from 'src/components/common/Types/CommonTypes';
import { nl2Br } from 'src/utils/DOMUtils';
import isEmpty from 'lodash/isEmpty';
import { Divider, Icon, Tooltip } from 'antd';
import moment from 'moment';
import { tokens } from 'src/design-system';

const styles = (theme) => ({
  cargoWeight: {
    border: 'none',
    boxShadow: 'none',
  },
  cargoWeightType: {
    '& .ant-select-selection': {
      border: 'none',
      boxShadow: 'none',
    },
  },
  additionalDetailsRow: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'center',
    marginRight: '30px',
    verticalAlign: 'middle',
  },
  label: {
    color: '#666666',
    fontSize: `${tokens.fontSize.small}px`,
    wordWrap: 'break-word',
    lineHeight: '12px',
  },
  addressCard: {
    whiteSpace: 'break-spaces',
    fontSize: '13px',
    color: tokens.colors.textSecondary,
    lineHeight: '18px',
  },
  labelInfo: {
    color: '#272727',
    fontSize: `${tokens.fontSize.body}px`,
    fontWeight: tokens.fontWeight.semibold,
    wordWrap: 'break-word',
    lineHeight: '14px',
    display: 'inline-block',
  },
  domesticLabel: {
    fontWeight: '400',
    fontSize: `${tokens.fontSize.small}px`,
    lineHeight: '16px',
    color: tokens.colors.textSecondary,
  },
  domesticRowTitle: {
    fontWeight: tokens.fontWeight.bold,
    fontSize: `${tokens.fontSize.body}px`,
    lineHeight: '19px',
    color: tokens.colors.text,
  },
  domesticDetails: {
    fontWeight: tokens.fontWeight.semibold,
    fontSize: `${tokens.fontSize.body}px`,
    lineHeight: '19px',
    color: '#272727',
  },
  stopLocationTag: {
    fontSize: '11px',
    padding: '2px 8px',
    marginLeft: '5px',
    borderRadius: '10px'
  },
  stopLocationTagPickup: {
    backgroundColor: '#fff0dc'
  },
  stopLocationTagDelivery: {
    backgroundColor: '#dceeff'
  }
});

interface Iprops extends StylesProps<ReturnType<typeof styles>> {
  parentClasses: any;
  isBookingSideDrawer: boolean;
  inquiryData: any;
  orgMaster: any;
  vehicleTypeSizeList: ContainerList[];
  currencies;
  bookingData;
  domesticVehicleTypeSizeList: ContainerList[];
  allowedDocumentTypes;
  customFieldsForInquiry;
}

const TruckingInquiryDetails = (props: Iprops) => {
  const {
    parentClasses,
    isBookingSideDrawer,
    inquiryData,
    classes,
    orgMaster,
    vehicleTypeSizeList,
    currencies,
    bookingData,
    domesticVehicleTypeSizeList,
  } = props;

  const isDomestic = inquiryData.mode === InquiryModes.DOMESTIC || inquiryData.mode === InquiryModes.RPTL;

  const renderTabHeaderDetails = () => {
    return (
      <div
        style={{ paddingBottom: '30px', color: tokens.colors.textSecondary, fontWeight: 600 }}
        className={isBookingSideDrawer ? parentClasses.bookingType : undefined}
      >
        {inquiryData.mode} {inquiryData.mode !== InquiryModes.DOMESTIC ? `| ${prettifyString(inquiryData.type)}` : ''}
      </div>
    );
  };

  const renderMovementDetail = (props) => {
    const { label, value, extraLabel, extraValue } = props;
    return (
      <Row style={{ padding: '5px 0px' }}>
        <Col span={7} className={classes.label}>
          {label}
          {extraLabel && <div style={{ marginTop: '4px', fontSize: `${tokens.fontSize.caption}px` }}>{extraLabel}</div>}
        </Col>
        <Col span={17} className={classes.labelInfo}>
          {value}
          {extraValue && (
            <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: 'normal' }}>
              {extraValue}
            </div>
          )}
        </Col>
      </Row>
    );
  };

  const renderCargoProduct = () => {
    return (
      <Row style={{ padding: '5px 0px', marginBottom: '18px' }}>
        <Col span={7} className={classes.label}>
          Cargo {inquiryData.productType ? '& Product' : ''}
        </Col>
        <Col span={17} className={classes.labelInfo}>
          {inquiryData?.cargoType ? prettifyString(inquiryData.cargoType) : '--'}
          {inquiryData?.productType ? ` - ${inquiryData?.productType}` : ''}
          {inquiryData?.temperatureControlled ? ` | ${inquiryData?.temperatureControlled}°C` : ''}
        </Col>
      </Row>
    );
  };

  const renderTermsConditions = () => {
    return (
      <div style={{ margin: '12px 0 0 16px' }}>
        <div className={classes.label}>Terms &amp; Conditions</div>
        <div className={classes.labelInfo} style={{ marginTop: '12px', lineHeight: '21px' }}>
          {inquiryData?.termsAndConditions ? nl2Br(inquiryData.termsAndConditions) : '--'}
        </div>
      </div>
    );
  };

  const renderAdditionalDetail = (label, value) => {
    const displayValue =
    typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || '--';
    return (
      <Row style={{ padding: '5px 0px', marginBottom: '18px' }}>
        <Col span={7} className={classes.label}>
          {label}
        </Col>
        <Col span={17} className={classes.labelInfo}>
          {displayValue}
        </Col>
      </Row>
    );
  };

  const renderVehicleDetail = () => {
    return (
      <Row style={{ padding: '5px 0px', marginBottom: '18px' }}>
        <Col span={7} className={classes.label}>
          Vehicle Details
        </Col>
        <Col span={17} className={classes.labelInfo}>
          {getTrailerOrDomesticVehicleList(inquiryData, vehicleTypeSizeList)}
        </Col>
      </Row>
    );
  };
  const renderOverWeight = () => {
    return (
      <Row style={{ padding: '5px 0px', marginBottom: '18px' }}>
        <Col span={7} className={classes.label}>
          Over Weight
        </Col>
        <Col span={17} className={classes.labelInfo}>
          {renderoverWeight(inquiryData?.otherDetails?.overWeight)}
        </Col>
      </Row>
    );
  };

  const renderContainerPickup = () => {
    // For DOMESTIC/RPTL mode, always show origin location details for Stuffing Location
    // For TRAILER mode, show based on type (EXPORT = empty container location, IMPORT = origin location)
    const containerPickup =
      (inquiryData.type === InquiryType.EXPORT && !isDomestic)
        ? (
          inquiryData.emptyContainerLocationPortDetails?.name || inquiryData.emptyContainerLocationPortDetails?.code
          ? `${inquiryData.emptyContainerLocationPortDetails?.name} (${inquiryData.emptyContainerLocationPortDetails?.code})` 
          : '-'
        ) 
        : (inquiryData.originLocationDetails?.name || inquiryData.originLocationDetails?.referenceNumber)
          ? `${inquiryData.originLocationDetails?.name} (${inquiryData.originLocationDetails?.referenceNumber})`
          : inquiryData.originLocationDetails?.city || '-';
    const extraLabel = (inquiryData.type === InquiryType.EXPORT && !isDomestic) ? '' : '(Origin)';
    const extraValue =
      (inquiryData.type === InquiryType.EXPORT && !isDomestic) ? '' : inquiryData.originLocationDetails;
    return (
      <React.Fragment>
        {renderMovementDetail({
          extraLabel,
          extraValue: renderAddressDetails({
            orgMaster,
            classes,
            item: extraValue,
          }),
          label: isDomestic ? 'Stuffing Location' : 'Container Pickup',
          value: containerPickup,
        })}
      </React.Fragment>
    );
  };

  const renderStopDetails = (stopLocationData, stopLocationIndex) => {
    const stopLocationAddress = stopLocationData?.address?.name ? `${stopLocationData?.address?.name} (${stopLocationData?.address?.referenceNumber})` : stopLocationData?.address;
    const stopLocationDisplayAddress = stopLocationData?.address?.displayAddress;
    const stopLocationType = stopLocationData?.type;
    return (
      <React.Fragment>
        {renderMovementDetail({
          label: <>
            <span>Stop {stopLocationIndex + 1}</span>
            <span className={`${classes.stopLocationTag} ${classes[`stopLocationTag${stopLocationType}`]}`}>{stopLocationType}</span>
          </>,
          value: <Tooltip title={stopLocationDisplayAddress}>{stopLocationAddress}</Tooltip>,
          extraValue: renderAddressDetails({
            orgMaster,
            classes,
            item: stopLocationData?.address,
          })
        })}
      </React.Fragment>
    );
  }

  const renderStuffingOrDestuffingLocation = () => {
    // For DOMESTIC/RPTL mode, always show De-Stuffing Location (destination)
    // For TRAILER mode, show based on type (EXPORT = Stuffing, IMPORT = De-Stuffing)
    const label =
      (inquiryData.type === InquiryType.EXPORT && !isDomestic) ? 'Stuffing Location' : 'De-Stuffing Location';
    const extraLabel = (inquiryData.type === InquiryType.EXPORT && !isDomestic) ? '(Origin)' : '(Destination)';
    const value =
      (inquiryData.type === InquiryType.EXPORT && !isDomestic)
        ? (
          inquiryData.originLocationDetails?.name || inquiryData.originLocationDetails?.referenceNumber
          ? `${inquiryData.originLocationDetails?.name} (${inquiryData.originLocationDetails.referenceNumber})`
          : inquiryData.originLocationDetails?.city ?? '-'
        ) 
        : (inquiryData.destinationLocationDetails?.name || inquiryData.destinationLocationDetails?.referenceNumber)
          ? `${inquiryData.destinationLocationDetails?.name} (${inquiryData.destinationLocationDetails.referenceNumber})`
          : inquiryData.destinationLocationDetails?.city ?? '-';
    const extraValue =
      (inquiryData.type === InquiryType.EXPORT && !isDomestic)
        ? inquiryData.originLocationDetails
        : inquiryData.destinationLocationDetails;
    return (
      <React.Fragment>
        {renderMovementDetail({
          extraLabel,
          label,
          value,
          extraValue: renderAddressDetails({
            orgMaster,
            classes,
            item: extraValue,
          }),
        })}
      </React.Fragment>
    );
  };

  const renderContainerDropOff = () => {
    const extraLabel = inquiryData.type === InquiryType.EXPORT ? '(Destination)' : '';
    const value =
      inquiryData.type === InquiryType.EXPORT
        ? (
          inquiryData.destinationPortDetails?.name || inquiryData.destinationPortDetails?.code 
          ? `${inquiryData.destinationPortDetails?.name} (${inquiryData.destinationPortDetails?.code})`
          : '-'
        )
        : (inquiryData.emptyContainerLocationPortDetails?.name || inquiryData.emptyContainerLocationPortDetails?.code)
          ? `${inquiryData.emptyContainerLocationPortDetails?.name} (${inquiryData.emptyContainerLocationPortDetails?.code})`
          : '-';
    return (
      <React.Fragment>
        {renderMovementDetail({
          extraLabel,
          value,
          label: 'Container Drop Off',
        })}
      </React.Fragment>
    );
  };

  const getReferenceRate = () => {
    return inquiryData?.referenceRate?.amount ? (
      <div>
        <span style={{ fontSize: tokens.fontSize.caption, verticalAlign: '20%' }}>
          {currencies[inquiryData.referenceRate?.currency]}
        </span>
        {Number(inquiryData.referenceRate?.amount).toFixed(2)}
        <span style={{ fontSize: 9, marginLeft: 5 }}>
          x {Number(inquiryData.totalUnits?.amount).toFixed(2)} {inquiryData.totalUnits?.format}
        </span>
        <span style={{ marginLeft: 5 }}>=</span>
        <span style={{ fontSize: tokens.fontSize.caption, verticalAlign: '20%', marginLeft: 5 }}>
          {currencies[inquiryData.referenceRate?.currency]}
        </span>
        {(
          Number(inquiryData.referenceRate?.amount) * Number(inquiryData.totalUnits?.amount)
        ).toFixed(2)}
      </div>
    ) : (
      '--'
    );
  };

  const renderBookingNote = () => {
    const document = bookingData?.confirmationDetails?.documents?.find(
      (item) => item.type === 'DO'
    );
    return (
      <Row style={{ padding: '5px 0px', marginBottom: '18px' }}>
        <Col span={7} className={classes.label}>
          {inquiryData?.type === InquiryType.EXPORT ? 'Booking Note' : 'DO'}
        </Col>
        <Col span={17} className={classes.labelInfo}>
          {!isEmpty(document) ? (
            <a style={{ fontSize: `${tokens.fontSize.small}px`, color: tokens.colors.primary }}
            onClick={() => convertBookingNotesToZip(bookingData)}>
            <Icon style={{ paddingRight: '4px', fontSize: `${tokens.fontSize.body}px` }} type="download" />
            {inquiryData?.type === InquiryType.EXPORT ? 'Booking Note': 'DO'} </a>
          ) : (
            '--'
          )}
        </Col>
      </Row>
    );
  };

  const renderInquiryDetails = () => {
    const { customFieldsForInquiry } = props;
    return (
      <Row gutter={24}>
        <Col span={12}>
          <Timeline className={parentClasses.antTimeline}>
            <Timeline.Item
              dot={<div className={[parentClasses.dot, parentClasses.dotFilled].join(' ')}></div>}
            >
              {renderContainerPickup()}
            </Timeline.Item>
            <Timeline.Item
              dot={<div className={[parentClasses.dot, parentClasses.dotFilled].join(' ')}></div>}
            >
              {renderStuffingOrDestuffingLocation()}
            </Timeline.Item>
            <Timeline.Item
              dot={<div className={[parentClasses.dot, parentClasses.dotFilled].join(' ')}></div>}
            >
              {renderContainerDropOff()}
            </Timeline.Item>
          </Timeline>
          {renderTermsConditions()}
        </Col>
        <Col span={12}>
          {renderVehicleDetail()}
          {renderCargoProduct()}
          {renderAdditionalDetail(
            'Cargo Ready Date',
            inquiryData.cargoReadyDate ? getDateValue(inquiryData.cargoReadyDate)?.format('DD MMM YYYY') : undefined
          )}
          {renderAdditionalDetail(
            'Contract Duration',
            inquiryData.preferredSailingRange &&
            inquiryData.preferredSailingRange[0] &&
            inquiryData.preferredSailingRange[1]
              ? `${getDateValue(inquiryData.preferredSailingRange[0])?.format('DD MMM YYYY')} - ${getDateValue(inquiryData.preferredSailingRange[1])?.format('DD MMM YYYY')}`
              : undefined
          )}
          {renderAdditionalDetail('Internal Reference No.', inquiryData.internalReferenceNumber)}
          {renderAdditionalDetail('Reference Rate', getReferenceRate())}
          {renderAdditionalDetail('Carrier Free Days ', inquiryData.carrierFreeDays)}
          {renderAdditionalDetail('Origin Free Days ', inquiryData.requiredOriginFreeDays)}
          {renderAdditionalDetail('Destination Free Days ', inquiryData.requiredDestinationFreeDays)}
          {(
            customFieldsForInquiry?.[inquiryData.mode]?.shipmentName || inquiryData?.otherDetails?.hasOwnProperty('shipmentName')
          ) && renderAdditionalDetail(
            customFieldsForInquiry?.[inquiryData.mode]?.shipmentName?.prettyName || 'Shipment Name',
            inquiryData?.otherDetails?.shipmentName,
          )}
          {(
            customFieldsForInquiry?.[inquiryData.mode]?.requestor || inquiryData?.otherDetails?.hasOwnProperty('requestor')
          ) && renderAdditionalDetail(
            customFieldsForInquiry?.[inquiryData.mode]?.requestor?.prettyName || 'Requestor',
            inquiryData?.otherDetails?.requestor,
          )}
          {(
            customFieldsForInquiry?.[inquiryData.mode]?.serviceLevel || inquiryData?.otherDetails?.hasOwnProperty('serviceLevel')
          ) && renderAdditionalDetail(
            customFieldsForInquiry?.[inquiryData.mode]?.serviceLevel?.prettyName || 'Service Level',
            inquiryData?.otherDetails?.serviceLevel,
          )}
          {(
            customFieldsForInquiry?.[inquiryData.mode]?.typeOfMovement || inquiryData?.otherDetails?.hasOwnProperty('typeOfMovement')
          ) && renderAdditionalDetail(
            customFieldsForInquiry?.[inquiryData.mode]?.typeOfMovement?.prettyName || 'Type of Import/Export',
            inquiryData?.otherDetails?.typeOfMovement,
          )}
          {(
            inquiryData?.otherDetails?.isDraft && (customFieldsForInquiry?.[inquiryData.mode]?.remarks || inquiryData?.otherDetails?.hasOwnProperty('remarks'))
          ) && renderAdditionalDetail(
            customFieldsForInquiry?.[inquiryData.mode]?.remarks?.prettyName || 'Remarks',
            inquiryData?.otherDetails?.remarks,
          )}
          {renderAdditionalDetail('Business Type', inquiryData?.otherDetails?.businessType)}
          {inquiryData?.otherDetails?.isGensetRequired !== undefined && (
          <>
          {renderAdditionalDetail('Genset Required',inquiryData?.otherDetails?.isGensetRequired)}
          {renderOverWeight()}
          </>
)}
          
          {inquiryData?.otherDetails?.businessEntity && renderAdditionalDetail('Business Entity', inquiryData?.otherDetails?.businessEntity)}
          {isBookingSideDrawer && renderBookingNote()}
        </Col>
      </Row>
    );
  };

  const renderDetailsItem = (label, value) => {
    return (
      <Row gutter={[10, 20]}>
        <Col span={8} className={classes.domesticLabel}>
          {label}
        </Col>
        <Col span={16} className={classes.domesticDetails}>
          {value || '--'}
        </Col>
      </Row>
    );
  };
  const renderRPTLProductDetails = (inquiryFFDetails: any) => {

    return (            <Col span={8} className={classes.labelInfo} style={{ paddingLeft: '7px' }}>
      {inquiryFFDetails?.productList && inquiryFFDetails?.productList.length > 0 ? (
        <Tooltip
          title={inquiryFFDetails?.productList
            .filter(product => product)
            .map((product) => {
             
              const parts: string[] = [];
              if (product?.hsCode) parts.push(product.hsCode);
              if (product?.description) parts.push(product.description);
              return parts.join(' - ');
            })
            .filter(desc => desc)
            .join(', ')}
        >
          <div>
            {inquiryFFDetails?.productList
              .filter(product => product)
              .map((product, index, array) => (
                <div key={index}>
                  {product.count ? `${product.count} X ` : ''}
                  {product.internalName ? `${product.internalName} ` : ''}
                  {product.referenceNumber ? `[${product.referenceNumber}] ` : ''}
                  {product.unit || ''}
                  {index < array.length - 1 ? ', ' : ''}
                </div>
              ))}
          </div>
        </Tooltip>
      ) : (
        '-'
      )}
    </Col>)
  }

  const renderDomesticBasicDetails = () => {
    return (
      <Row gutter={[20, 0]}>
        <Col span={4} className={classes.domesticRowTitle}>
          Basic Details
        </Col>
        <Col span={10}>
          <Timeline className={parentClasses.antTimeline}>
            <Timeline.Item
              dot={<div className={[parentClasses.dot, parentClasses.dotFilled].join(' ')}></div>}
            >
              {renderContainerPickup()}
            </Timeline.Item>
            {
              inquiryData?.otherDetails?.stops?.map((stopLocationData, index) => {
                return (
                  <Timeline.Item
                    dot={<div className={parentClasses.dot}></div>}
                  >
                    {renderStopDetails(stopLocationData, index)}
                  </Timeline.Item>
                )
              })
            }
            <Timeline.Item
              dot={<div className={[parentClasses.dot, parentClasses.dotFilled].join(' ')}></div>}
            >
              {renderStuffingOrDestuffingLocation()}
            </Timeline.Item>
          </Timeline>
          <div style={{ display: 'grid', gridTemplateColumns: '143px max-content' }}>
            <div className={classes.domesticLabel}>Total Distance</div>
            <div className={classes.domesticDetails}>
              {
                isValidNumber(inquiryData?.otherDetails?.distance?.value) ? 
                `${inquiryData?.otherDetails?.distance?.value} ${inquiryData?.otherDetails?.distance?.unit || 'km'}` :
                '-'
              }
            </div>
          </div>
        </Col>
        <Col span={10}>
          <Row gutter={[0, 45]}>
            <Col>
              {renderDetailsItem('Cargo & Product', inquiryData?.cargoType
                    ? `${prettifyString(inquiryData.cargoType)} ${inquiryData?.productType?
                      `,${inquiryData?.productType}`:''}
                    ${inquiryData?.temperatureControlled
                      ? ` | ${inquiryData?.temperatureControlled}°C`
                      : ''
                    }`
                    : '-')}
            </Col>
            {inquiryData.mode !== InquiryModes.RPTL && inquiryData?.productList?.length > 0 ? (
              <Row>
               <Col span={8} className={classes.domesticLabel}>
                Product Details
               </Col>
              <Col>
                {renderRPTLProductDetails(inquiryData)}
              </Col>
              </Row>
            ) : inquiryData.mode !== InquiryModes.RPTL ? (
            <Col>
              {renderDetailsItem(
                'Product',
                inquiryData?.product ? `${inquiryData?.product?.internalName} ${inquiryData?.product?.referenceNumber ? `[${inquiryData?.product?.referenceNumber}]` : ''}` : '-'
              )}
            </Col>
            ) : (
              <Row>
               <Col span={8} className={classes.domesticLabel}>
                Product Details
               </Col>
              <Col>
                {renderRPTLProductDetails(inquiryData)}
              </Col>
              </Row>
            )}
          </Row>
        </Col>
      </Row>
    );
  };

  const renderDomesticVehicleDetails = () => {
    return (
      <Row gutter={10}>
        <Col span={4} className={classes.domesticRowTitle}>
          Vehicle Details
        </Col>
        <Col span={3} className={classes.domesticLabel}>
          Vehicle Details
        </Col>
        <Col span={16} style={{ marginLeft: '10px' }} className={classes.domesticDetails}>
          {getTrailerOrDomesticVehicleList(inquiryData, domesticVehicleTypeSizeList)}
        </Col>
      </Row>
    );
  };

  const renderDomesticPackagingDetails = () => {
    return (
      <Row gutter={10}>
        <Col span={4} className={classes.domesticRowTitle}>
          Packaging Details
        </Col>
        <Col span={20}>
          {inquiryData?.containerDetails?.map((pkg, index) => {
            const pkgType = prettifyString(pkg?.type);
            return (
              <>
                <Row gutter={[10, 20]}>
                  <Col span={12}>
                    {renderDetailsItem(
                      'Quantity & Type',
                      `${pkg?.count}, ${pkgType}`
                    )}
                    {renderDetailsItem(
                      'Dimension (L x B x H)',
                      pkg?.packageDimension
                        ? `${pkg?.packageDimension?.length} x ${pkg?.packageDimension?.breadth} x
                        ${pkg?.packageDimension?.height} ${pkg?.packageDimension?.unit}`
                        : ''
                    )}
                  </Col>
                  <Col span={12}>
                    {renderDetailsItem(
                      'Total Gross Wt.',
                      `${((pkg?.count || 0) * (pkg?.weight?.value || 0))?.toLocaleString()} ${pkg?.weight?.unit}`
                    )}
                    {renderDetailsItem(
                      'Total Volume',
                      `${((pkg?.count || 0) * (pkg?.volume?.value || 0))?.toLocaleString()} ${pkg?.volume?.unit}`
                    )}
                  </Col>
                </Row>
                {index + 1 !== inquiryData?.containerDetails?.length ? <Divider /> : ''}
              </>
            );
          })}
        </Col>
      </Row>
    );
  };

  const renderDomesticOtherDetails = () => {
    const isPreferredSailingValid = inquiryData?.preferredSailingRange?.length === 2;
    const { allowedDocumentTypes, customFieldsForInquiry } = props;
    const numberFormater = new Intl.NumberFormat('en-US')
    const cargoValueAmount = inquiryData?.otherDetails?.cargoValue?.amount;
    const cargoValue = cargoValueAmount ? `${numberFormater.format(cargoValueAmount)} ${inquiryData?.otherDetails?.cargoValue?.currency}` : '--';
    return (
      <Row gutter={10}>
        <Col span={4} className={classes.domesticRowTitle}>
          Other Details
        </Col>
        <Col span={10}>
          {renderDetailsItem('Internal Reference Number', inquiryData?.internalReferenceNumber)}
          {/* {renderDetailsItem('Stackable', inquiryData?.stackable ? 'Yes' : 'No')} */}
          {renderDetailsItem('Reference Rate', getReferenceRate())}
          {renderDetailsItem('Business Type', inquiryData?.otherDetails?.businessType)}
          {inquiryData?.otherDetails?.businessEntity && renderDetailsItem('Business Entity', inquiryData?.otherDetails?.businessEntity)}
          {renderDetailsItem(
            'Other Details', 
            inquiryData?.otherDetails?.extraDetails ? nl2Br(inquiryData?.otherDetails?.extraDetails) : ''
          )}
          {(customFieldsForInquiry?.[inquiryData.mode]?.shipmentName || inquiryData?.otherDetails?.hasOwnProperty('shipmentName')
          ) && renderDetailsItem(
            customFieldsForInquiry?.[inquiryData.mode]?.shipmentName?.prettyName || 'Shipment Name',
            inquiryData?.otherDetails?.shipmentName,
          )}
          {(customFieldsForInquiry?.[inquiryData.mode]?.requestor || inquiryData?.otherDetails?.hasOwnProperty('requestor')
          ) && renderDetailsItem(
            customFieldsForInquiry?.[inquiryData.mode]?.requestor?.prettyName || 'Requestor',
            inquiryData?.otherDetails?.requestor,
          )}
          {(customFieldsForInquiry?.[inquiryData.mode]?.serviceLevel || inquiryData?.otherDetails?.hasOwnProperty('serviceLevel')
          ) && renderDetailsItem(
            customFieldsForInquiry?.[inquiryData.mode]?.serviceLevel?.prettyName || 'Service Level',
            inquiryData?.otherDetails?.serviceLevel,
          )}
          {(customFieldsForInquiry?.[inquiryData.mode]?.typeOfMovement || inquiryData?.otherDetails?.hasOwnProperty('typeOfMovement')
          ) && renderDetailsItem(
            customFieldsForInquiry?.[inquiryData.mode]?.typeOfMovement?.prettyName || 'Type of Import/Export',
            inquiryData?.otherDetails?.typeOfMovement,
          )}
          {(inquiryData?.otherDetails?.isDraft && (customFieldsForInquiry?.[inquiryData.mode]?.remarks || inquiryData?.otherDetails?.hasOwnProperty('remarks'))
          ) && renderDetailsItem(
            customFieldsForInquiry?.[inquiryData.mode]?.remarks?.prettyName || 'Remarks',
            inquiryData?.otherDetails?.remarks,
          )}
          {renderDetailsItem(
            'Terms & Conditions',
            inquiryData.termsAndConditions ? nl2Br(inquiryData.termsAndConditions) : ''
          )}
        </Col>
        <Col span={10}>
          {renderDetailsItem('High Value Cargo', inquiryData?.otherDetails?.isHighValueInsuranceRequired ? 'Yes' : 'No')}
          {renderDetailsItem('Cargo Value', <span>{cargoValue}</span>)}
          {renderDetailsItem(
            'Contract Duration',
            isPreferredSailingValid
              ? `${moment(inquiryData?.preferredSailingRange[0]).format('DD MMM YYYY')} -
                  ${moment(inquiryData?.preferredSailingRange[1]).format('DD MMM YYYY')}`
              : ''
          )}
          {renderDetailsItem(
            'Inquiry Closing Date',
            inquiryData?.deadline ? moment(inquiryData?.deadline).format('DD MMM YYYY') : '--'
          )}
          {renderDetailsItem(
            'Cargo Ready Date',
            inquiryData?.cargoReadyDate ? moment(inquiryData?.cargoReadyDate).format('DD MMM YYYY') : '--'
          )}
          <Row gutter={[10, 20]}>
            <Col span={8} className={classes.domesticLabel}>
              Documents
            </Col>
            <Col span={16}>
              {inquiryData?.documentUrls?.length
                ? inquiryData?.documentUrls?.map((doc) => {
                    return (
                      <div className={classes.domesticDetails} style={{ marginBottom: '20px' }}>
                        <a
                          key={doc?.url}
                          href={doc?.url}
                          style={{ color: tokens.colors.primary, fontSize: `${tokens.fontSize.body}px`, fontWeight: 'bold' }}
                        >
                          {allowedDocumentTypes?.[doc?.type]}
                        </a>
                      </div>
                    );
                  })
                : '--'}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderDomesticInquiryDetails = () => {
    return (
      <Row gutter={20}>
        <Col>{renderDomesticBasicDetails()}</Col>
        <Divider />
        {inquiryData.mode ===  InquiryModes.RPTL && (
          <>
            <Col>{renderDomesticPackagingDetails()}</Col>
            <Divider />
          </>
        )}
        {inquiryData.mode !== InquiryModes.RPTL && (
          <>
            <Col>{renderDomesticVehicleDetails()}</Col>
            <Divider />
          </>
        )}
        <Col>{renderDomesticOtherDetails()}</Col>
      </Row>
    );
  };

  return (
    <div>
      <Card
        className={
          isBookingSideDrawer ? parentClasses.bookingSideDrawer : parentClasses.bookindDetailsDiv
        }
      >
        {renderTabHeaderDetails()}
        {isDomestic ? renderDomesticInquiryDetails() : renderInquiryDetails()}
      </Card>
    </div>
  );
};

const mapStateToProps = ({ masterData, inquiryReducer, organisation }, {}) => ({
  masterData: inquiryReducer.masterData,
  cargoTypes: masterData.cargoTypeList,
  cargoList: masterData.cargoList,
  currencies: masterData.currencySymbols,
  orgMaster: organisation.orgMaster,
  vehicleTypeSizeList: masterData.vehicleTypeSizeList,
  domesticVehicleTypeSizeList: masterData.domesticVehicleTypeSizeList,
  allowedDocumentTypes: inquiryReducer.masterData.allowedDocumentTypes,
  customFieldsForInquiry: masterData?.customFieldsForInquiry
});

export default Form.create<any>()(
  connect(mapStateToProps)(withStyles(styles)(React.memo(TruckingInquiryDetails)))
);
